/*
  PDP gallery arrows -- live's `.left-arrow-2` / `.right-arrow-3`.

  Live's gallery is a Webflow `w-slider` with `data-infinite="true"`, so its
  arrows WRAP: past the last image is the first. Ours reproduces that.

  IT DRIVES DAWN'S OWN THUMBNAILS RATHER THAN THE MEDIA LIST. `media-gallery.js`
  already binds every `[data-target]` thumbnail button to `setActiveMedia`,
  which owns the active class, the `aria-current`, the announcement in
  `#GalleryStatus`, and the variant sync. Clicking the neighbouring thumbnail
  therefore inherits all of it, and there is no second source of truth for which
  media is showing -- the failure mode a parallel index would have.

  Section-scoped and idempotent by construction: a custom element connects when
  Shopify re-renders the section and disconnects when it is removed, so there is
  no `shopify:section:load` wiring to leak. The listener is on the element
  itself, so a re-render cannot double-bind.

  No motion: swapping media is a `display` toggle in Dawn, so there is nothing
  for `prefers-reduced-motion` to reduce. The controls are real `<button>`s, so
  Tab/Enter/Space work without any key handling here.
*/
if (!customElements.get('pure-gallery-arrows')) {
  customElements.define(
    'pure-gallery-arrows',
    class PureGalleryArrows extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;

        // Fewer than two media means the arrows have nothing to move to. Live
        // renders them regardless (Webflow does not check), but an arrow that
        // provably cannot change anything is a keyboard stop that lies about
        // what it does -- recorded as a deliberate deviation in
        // `commerce-family-build-spec.md`.
        if (this.thumbnails().length < 2) return;

        this.onClick = (event) => {
          const button = event.target.closest('[data-pure-gallery-step]');
          if (!button) return;
          this.step(Number(button.dataset.pureGalleryStep));
        };
        this.addEventListener('click', this.onClick);
        this.hidden = false;
      }

      disconnectedCallback() {
        if (this.onClick) this.removeEventListener('click', this.onClick);
      }

      thumbnails() {
        const list = this.gallery && this.gallery.querySelector('[id^="GalleryThumbnails"]');
        return list ? Array.from(list.querySelectorAll('[data-target]')) : [];
      }

      step(delta) {
        const items = this.thumbnails();
        if (items.length < 2) return;
        // `aria-current` is what `setActiveMedia` maintains, so it is the one
        // reading that cannot drift from what is painted.
        const current = items.findIndex((item) => item.querySelector('button[aria-current]'));
        const from = current === -1 ? 0 : current;
        const next = items[(from + delta + items.length) % items.length];
        const button = next && next.querySelector('button');
        if (button) button.click();
      }
    }
  );
}
